/**
 * CORE-1692 — ссылка на публикацию: `/u/{authorUserId}/post/{postId}`.
 *
 * Тот же адрес, что раздаёт «Поделиться постом» (CORE-630) и что кладёт в
 * `deeplink` уведомление о посте «Для подписчиков» (CORE-1708). Разбор
 * ручной и без зависимостей от RN: им живут и лента уведомлений, и переход
 * по push на устройстве, и оба проверяются под jest.
 */
export interface PostLink {
  authorUserId: string;
  postId: number;
}

/** Абсолютный адрес, путь или путь с query/hash → части ссылки; иначе null. */
export function parsePostLink(raw: unknown): PostLink | null {
  if (typeof raw !== 'string') return null;
  let path = raw.trim();
  if (!path) return null;
  // Хост отрезаем сами: `URL` у полифилла RN не везде полный.
  const schemeless = path.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
  path = schemeless.split(/[?#]/)[0];
  const m = path.match(/^\/u\/([^/]+)\/post\/(\d+)\/?$/);
  if (!m) return null;
  const postId = Number(m[2]);
  if (!Number.isInteger(postId) || postId <= 0) return null;
  let authorUserId = m[1];
  try {
    authorUserId = decodeURIComponent(authorUserId);
  } catch {
    /* оставляем как есть */
  }
  return { authorUserId, postId };
}

/** Ссылка на публикацию в том виде, в каком её строит бэкенд и «Поделиться». */
export function buildPostPath(authorUserId: string, postId: number): string {
  return `/u/${encodeURIComponent(authorUserId)}/post/${postId}`;
}

/**
 * CORE-1811 — id комментария, к которому ведёт уведомление: параметр навигации
 * `commentId` или `?comment=` из ссылки. Некорректное значение — `undefined`.
 */
export function focusCommentFrom(
  params: { commentId?: number | string; comment?: number | string } | undefined,
): number | undefined {
  const n = Number(params?.commentId ?? params?.comment);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** CORE-1811 — `?comment={id}` из ссылки на публикацию, если он есть. */
export function commentIdFromLink(raw: unknown): number | undefined {
  if (typeof raw !== 'string') return undefined;
  const m = raw.match(/[?&]comment=(\d+)/);
  return m ? focusCommentFrom({ comment: m[1] }) : undefined;
}
